// UserManagement.js — Admin page: manage external OTP users + internal role overrides
// Place in: reporting-frontend/src/pages/UserManagement.js

import { useState, useEffect, useCallback } from 'react';
import api from '../api';

const ROLES = ['admin', 'editor', 'readonly'];

const ROLE_BADGE = {
  admin:    { background: '#fef3c7', color: '#92400e' },
  editor:   { background: '#dbeafe', color: '#1e40af' },
  readonly: { background: '#f3f4f6', color: '#6b7280' },
};

export default function UserManagement() {
  const [tab,            setTab]            = useState('external');
  const [externalUsers,  setExternalUsers]  = useState([]);
  const [internalRoles,  setInternalRoles]  = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState('');
  const [success,        setSuccess]        = useState('');

  // Add-user form
  const [newEmail,       setNewEmail]       = useState('');
  const [newName,        setNewName]        = useState('');
  const [newRole,        setNewRole]        = useState('readonly');
  const [adding,         setAdding]         = useState(false);

  // Internal role form
  const [intEmail,       setIntEmail]       = useState('');
  const [intRole,        setIntRole]        = useState('editor');
  const [addingInt,      setAddingInt]      = useState(false);

  // Inline editing
  const [editId,         setEditId]         = useState(null);
  const [editRole,       setEditRole]       = useState('');

  const flash = (msg, isError = false) => {
    if (isError) setError(msg); else setSuccess(msg);
    setTimeout(() => { setError(''); setSuccess(''); }, 4000);
  };

  const loadExternal = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/v1/users/external');
      setExternalUsers(res.data);
    } catch {
      flash('Failed to load external users.', true);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadInternal = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/v1/users/internal-roles');
      setInternalRoles(res.data);
    } catch {
      flash('Failed to load internal roles.', true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'external') loadExternal();
    else loadInternal();
  }, [tab, loadExternal, loadInternal]);

  // ── External: add user ────────────────────────────────────────────────────
  const handleAddExternal = async () => {
    if (!newEmail.trim()) return;
    setAdding(true);
    try {
      await api.post('/api/v1/users/external', {
        email:        newEmail.trim(),
        display_name: newName.trim() || null,
        role:         newRole,
      });
      flash(`${newEmail.trim()} added successfully.`);
      setNewEmail(''); setNewName(''); setNewRole('readonly');
      loadExternal();
    } catch (err) {
      flash(err?.response?.data?.detail || 'Failed to add user.', true);
    } finally {
      setAdding(false);
    }
  };

  // ── External: inline role save ────────────────────────────────────────────
  const handleRoleSave = async (userId) => {
    try {
      await api.patch(`/api/v1/users/external/${userId}`, { role: editRole });
      flash('Role updated.');
      setEditId(null);
      loadExternal();
    } catch {
      flash('Failed to update role.', true);
    }
  };

  // ── External: deactivate ──────────────────────────────────────────────────
  const handleDeactivate = async (userId, email) => {
    if (!window.confirm(`Deactivate ${email}? They will no longer be able to sign in.`)) return;
    try {
      await api.delete(`/api/v1/users/external/${userId}`);
      flash(`${email} deactivated.`);
      loadExternal();
    } catch {
      flash('Failed to deactivate.', true);
    }
  };

  // ── Internal: set role ────────────────────────────────────────────────────
  const handleSetInternal = async () => {
    if (!intEmail.trim()) return;
    setAddingInt(true);
    try {
      await api.post('/api/v1/users/internal-roles', {
        email: intEmail.trim(),
        role:  intRole,
      });
      flash(`Role set for ${intEmail.trim()}.`);
      setIntEmail('');
      loadInternal();
    } catch (err) {
      flash(err?.response?.data?.detail || 'Failed to set role.', true);
    } finally {
      setAddingInt(false);
    }
  };

  // ── Internal: remove override ─────────────────────────────────────────────
  const handleRemoveInternal = async (email) => {
    if (!window.confirm(`Remove role override for ${email}? They will revert to 'editor'.`)) return;
    try {
      await api.delete(`/api/v1/users/internal-roles/${encodeURIComponent(email)}`);
      flash(`Override removed for ${email}.`);
      loadInternal();
    } catch {
      flash('Failed to remove override.', true);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {[
          { key: 'external', label: 'External users (OTP)' },
          { key: 'internal', label: 'Internal roles (Entra)' },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '6px 16px',
              borderRadius: 6,
              border: '1px solid #e5e5e5',
              background: tab === t.key ? '#1a1a1a' : '#fff',
              color:      tab === t.key ? '#fff'    : '#333',
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Flash messages */}
      {success && <div className="error" style={{ background: '#f0fdf4', borderColor: '#bbf7d0', color: '#166534' }}>{success}</div>}
      {error   && <div className="error">{error}</div>}

      {/* ── EXTERNAL USERS TAB ── */}
      {tab === 'external' && (
        <>
          {/* Add user form */}
          <div style={styles.formCard}>
            <h3 style={styles.formTitle}>Register external user</h3>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '2 1 200px' }}>
                <label style={styles.label}>Email</label>
                <input
                  style={styles.input}
                  type="email"
                  placeholder="user@customer.com"
                  value={newEmail}
                  onChange={e => setNewEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddExternal()}
                />
              </div>
              <div style={{ flex: '2 1 160px' }}>
                <label style={styles.label}>Display name (optional)</label>
                <input
                  style={styles.input}
                  type="text"
                  placeholder="Jane Smith"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                />
              </div>
              <div style={{ flex: '1 1 120px' }}>
                <label style={styles.label}>Role</label>
                <select style={styles.input} value={newRole} onChange={e => setNewRole(e.target.value)}>
                  {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <button
                style={{ ...styles.btn, flexShrink: 0, marginBottom: 0 }}
                onClick={handleAddExternal}
                disabled={adding || !newEmail.trim()}
              >
                {adding ? 'Adding…' : 'Add user'}
              </button>
            </div>
          </div>

          {/* Users table */}
          {loading ? (
            <div className="loading">Loading…</div>
          ) : (
            <div className="card" style={{ marginTop: 0 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Email', 'Name', 'Role', 'Status', 'Last login', 'Actions'].map(h => (
                      <th key={h} style={styles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {externalUsers.length === 0 && (
                    <tr><td colSpan={6} style={{ padding: '24px', textAlign: 'center', color: '#999', fontSize: 13 }}>No external users registered yet.</td></tr>
                  )}
                  {externalUsers.map(u => (
                    <tr key={u.id} style={{ opacity: u.is_active ? 1 : 0.5 }}>
                      <td style={styles.td}>{u.email}</td>
                      <td style={styles.td}>{u.display_name || <span style={{ color: '#aaa' }}>—</span>}</td>
                      <td style={styles.td}>
                        {editId === u.id ? (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <select
                              style={{ ...styles.input, marginBottom: 0, padding: '3px 8px', fontSize: 12 }}
                              value={editRole}
                              onChange={e => setEditRole(e.target.value)}
                            >
                              {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                            <button style={styles.saveBtn} onClick={() => handleRoleSave(u.id)}>Save</button>
                            <button style={styles.cancelBtn} onClick={() => setEditId(null)}>✕</button>
                          </div>
                        ) : (
                          <span
                            style={{ ...styles.badge, ...ROLE_BADGE[u.role], cursor: 'pointer' }}
                            title="Click to change role"
                            onClick={() => { setEditId(u.id); setEditRole(u.role); }}
                          >
                            {u.role}
                          </span>
                        )}
                      </td>
                      <td style={styles.td}>
                        <span className={`badge ${u.is_active ? 'badge-active' : 'badge-inactive'}`}>
                          {u.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        {u.last_login
                          ? new Date(u.last_login).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                          : <span style={{ color: '#aaa' }}>Never</span>}
                      </td>
                      <td style={styles.td}>
                        {u.is_active && (
                          <button
                            style={styles.dangerBtn}
                            onClick={() => handleDeactivate(u.id, u.email)}
                          >
                            Deactivate
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── INTERNAL ROLES TAB ── */}
      {tab === 'internal' && (
        <>
          <div style={{ ...styles.formCard, marginBottom: 16 }}>
            <h3 style={styles.formTitle}>Set role override</h3>
            <p style={{ fontSize: 12, color: '#888', margin: '0 0 12px' }}>
              Internal (@starlims.com) users default to <strong>editor</strong> unless overridden here.
              Add a row only when you need to grant admin or restrict to readonly.
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '3 1 220px' }}>
                <label style={styles.label}>Email (@starlims.com)</label>
                <input
                  style={styles.input}
                  type="email"
                  placeholder="name@starlims.com"
                  value={intEmail}
                  onChange={e => setIntEmail(e.target.value)}
                />
              </div>
              <div style={{ flex: '1 1 120px' }}>
                <label style={styles.label}>Role</label>
                <select style={styles.input} value={intRole} onChange={e => setIntRole(e.target.value)}>
                  {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <button
                style={{ ...styles.btn, flexShrink: 0, marginBottom: 0 }}
                onClick={handleSetInternal}
                disabled={addingInt || !intEmail.trim()}
              >
                {addingInt ? 'Saving…' : 'Set role'}
              </button>
            </div>
          </div>

          {loading ? (
            <div className="loading">Loading…</div>
          ) : (
            <div className="card" style={{ marginTop: 0 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Email', 'Role override', 'Set on', 'Actions'].map(h => (
                      <th key={h} style={styles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {internalRoles.length === 0 && (
                    <tr><td colSpan={4} style={{ padding: '24px', textAlign: 'center', color: '#999', fontSize: 13 }}>No overrides set — all internal users are defaulting to editor.</td></tr>
                  )}
                  {internalRoles.map(u => (
                    <tr key={u.email}>
                      <td style={styles.td}>{u.email}</td>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, ...ROLE_BADGE[u.role] }}>{u.role}</span>
                      </td>
                      <td style={styles.td}>
                        {new Date(u.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </td>
                      <td style={styles.td}>
                        <button style={styles.dangerBtn} onClick={() => handleRemoveInternal(u.email)}>
                          Remove override
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const styles = {
  formCard: {
    background: '#fafaf9',
    border: '1px solid #e5e5e5',
    borderRadius: 8,
    padding: '16px 20px',
    marginBottom: 16,
  },
  formTitle: { fontSize: 14, fontWeight: 600, margin: '0 0 12px', color: '#1a1a1a' },
  label:     { display: 'block', fontSize: 11, fontWeight: 500, color: '#666', marginBottom: 4 },
  input: {
    width: '100%',
    padding: '7px 10px',
    fontSize: 13,
    border: '1px solid #e0e0e0',
    borderRadius: 6,
    boxSizing: 'border-box',
    background: '#fff',
    marginBottom: 0,
  },
  btn: {
    padding: '8px 18px',
    background: '#1a1a1a',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    marginBottom: 0,
  },
  th:        { padding: '9px 14px', textAlign: 'left', fontWeight: 500, fontSize: 12, color: '#666', borderBottom: '1px solid #e5e5e5' },
  td:        { padding: '9px 14px', borderBottom: '1px solid #f0f0ee', color: '#1a1a1a', fontSize: 13 },
  badge:     { display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 500 },
  saveBtn:   { padding: '3px 10px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 4, fontSize: 12, cursor: 'pointer' },
  cancelBtn: { padding: '3px 8px', background: '#e5e5e5', color: '#333', border: 'none', borderRadius: 4, fontSize: 12, cursor: 'pointer' },
  dangerBtn: { padding: '3px 10px', background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 4, fontSize: 12, cursor: 'pointer' },
};
